/**
 * PhysicalDof — depth of field from a LENS instead of a "focus range in
 * metres" (2026-09-25, visual realism R4).
 *
 * Thin-lens circle of confusion (diameter on the sensor):
 *     c(z) = (f² / N) · |z − s| / (z · (s − f))
 * f focal length, N f-number (aperture A = f/N), s focus distance, z the
 * surface distance — all in the same unit. On a sensor `sensorMm` wide imaged
 * onto `imageWidthPx`, c_px = c / sensorMm × imageWidthPx.
 *
 * The far field is BOUNDED: c(∞) = f² / (N (s − f)). At f = 35 mm, f/5.6 and
 * a player 3 m away that is ≈ 4 px across 1920 — the "subtle, near-hyperfocal
 * far field" a real camera gives, where the engine's old fixed 18 m / 55 m
 * range blurred every vista beyond ~73 m into a tilt-shift miniature. The
 * same lens at f/2 (a dialogue shot) is ≈ 11 px.
 *
 * three's `dof()` takes (focusDistance, focusRange, bokehScale) with
 * CoC = smoothstep(0, range, |z − s|) and a blur RADIUS of bokehScale × CoC
 * pixels, so `physicalDofParams` maps the lens onto it: bokehScale = c(∞)/2
 * (the far asymptote as a radius) and range = 2s (the physical CoC reaches
 * half its far value at z = 2s, where the smoothstep reaches half at range/2).
 */

export interface LensParams {
  /** f-number (5.6 = the everyday default; 2 = cinematic). */
  fNumber: number;
  /** Focal length in mm (default 35). */
  focalMm?: number;
  /** Sensor WIDTH in mm (default 36 — full frame). */
  sensorMm?: number;
}

/** CoC DIAMETER in pixels for a surface at `zM` metres with focus at `sM` metres. */
export function cocDiameterPx(zM: number, sM: number, lens: LensParams, imageWidthPx: number): number {
  const f = (lens.focalMm ?? 35) / 1000, N = Math.max(0.5, lens.fNumber), sensor = (lens.sensorMm ?? 36) / 1000;
  const s = Math.max(f * 1.01, sM), z = Math.max(1e-3, zM);
  const c = ((f * f) / N) * Math.abs(z - s) / (z * (s - f));
  return (c / sensor) * imageWidthPx;
}

/** The far-field (z → ∞) CoC diameter in pixels. */
export function cocInfinityPx(sM: number, lens: LensParams, imageWidthPx: number): number {
  const f = (lens.focalMm ?? 35) / 1000, N = Math.max(0.5, lens.fNumber), sensor = (lens.sensorMm ?? 36) / 1000;
  const s = Math.max(f * 1.01, sM);
  return ((f * f) / (N * (s - f)) / sensor) * imageWidthPx;
}

/** Map a lens focused at `sM` onto three's dof() inputs. */
export function physicalDofParams(sM: number, lens: LensParams, imageWidthPx: number): { focusDistance: number; focusRange: number; bokehScale: number; cocInfinityPx: number } {
  const cinf = cocInfinityPx(sM, lens, imageWidthPx);
  return { focusDistance: sM, focusRange: Math.max(0.5, 2 * sM), bokehScale: cinf / 2, cocInfinityPx: cinf };
}
